// @ts-nocheck
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import { 
  ChevronRight, 
  Building2, 
  MapPin, 
  FileText, 
  Users, 
  MessageSquare, 
  ShieldCheck,
  Clock,
  ArrowLeft,
  X,
  ExternalLink,
  FileSignature, 
  Send,          
  CheckCircle,
  Percent,
  DollarSign,
  CreditCard,
  UploadCloud,
  Calendar,
  Loader2,
  Zap,
  Files,
  Trash2,
  Search
} from "lucide-react";

import { getDocs, collection, query, where, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"; 
import { db, storage, storageEnvironment } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { gerarEnvelopeClickSign } from "../../services/clickSignService";

// ==========================================
// FUNÇÕES AUXILIARES
// ==========================================

// Soma de Valores Monetários em String
const calcularSoma = (val1, val2) => {
  const parse = (v) => parseFloat(String(v || "0").replace(/\./g, "").replace(",", ".")) || 0;
  const sum = parse(val1) + parse(val2);
  return sum.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ==========================================
// COMPONENTE: SKELETON LOADING
// ==========================================
const DetailsSkeleton = () => (
  <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-pulse bg-slate-50 min-h-full">
    <div className="h-4 w-48 bg-slate-200 rounded-md mb-4"></div>
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="h-8 w-64 bg-slate-200 rounded-lg mb-4"></div>
      <div className="flex gap-4">
        <div className="h-4 w-32 bg-slate-200 rounded-md"></div>
        <div className="h-4 w-32 bg-slate-200 rounded-md"></div>
      </div>
    </div>
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-1 space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 h-64">
          <div className="h-6 w-40 bg-slate-200 rounded-lg mb-6"></div>
          <div className="space-y-3">
            <div className="h-4 w-full bg-slate-200 rounded-md"></div>
            <div className="h-4 w-3/4 bg-slate-200 rounded-md"></div>
            <div className="h-4 w-1/2 bg-slate-200 rounded-md"></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 h-32"></div>
      </div>
      <div className="xl:col-span-2">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 min-h-[400px]">
          <div className="h-6 w-48 bg-slate-200 rounded-lg mb-6"></div>
          <div className="space-y-4">
            <div className="h-20 w-full bg-slate-200 rounded-xl"></div>
            <div className="h-20 w-full bg-slate-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ==========================================
// PÁGINA PRINCIPAL
// ==========================================
const SolicitacaoDetalhesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { companyInfo } = useAuth();
  
  const protocoloReal = id ? id.replace("-", "/") : "";

  // 🟢 BLINDAGEM DE TIPAGEM E DADOS PRINCIPAIS
  const [solicitacao, setSolicitacao] = useState(/** @type {any} */ (null));
  const [partes, setPartes] = useState(/** @type {any[]} */ ([]));
  const [loading, setLoading] = useState(true);

  // 🟢 DADOS DO BANCO DE TESTEMUNHAS (Etapa 2)
  const [bancoTestemunhas, setBancoTestemunhas] = useState(/** @type {any[]} */ ([]));
  const [selectedTestemunhas, setSelectedTestemunhas] = useState(/** @type {any[]} */ ([]));

  // 🟢 ESTADO: Acompanhamento de Fluxos (Novo Roadmap)
  const [contractStatus, setContractStatus] = useState(/** @type {string | null} */ (null));
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [parteSelecionada, setParteSelecionada] = useState(/** @type {any} */ (null));

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Modal 1: ClickSign Envelope
  const [isClickSignModalOpen, setIsClickSignModalOpen] = useState(false);
  const [clickSignFiles, setClickSignFiles] = useState([]);

  // Modal 2: Manual Upload Assinado
  const [isUploadSignedModalOpen, setIsUploadSignedModalOpen] = useState(false);
  const [signedFiles, setSignedFiles] = useState([]);
  const [signatureDate, setSignatureDate] = useState("");

  // 🟢 EFEITO PARA TRAVAR O SCROLL DA TELA QUANDO OS MODAIS ABRIREM
  useEffect(() => {
    if (isDocModalOpen || isClickSignModalOpen || isUploadSignedModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isDocModalOpen, isClickSignModalOpen, isUploadSignedModalOpen]);

  // 🟢 BUSCA DE TESTEMUNHAS
  useEffect(() => {
    if (!companyInfo?.id) return;
    const fetchTestemunhas = async () => {
      try {
        const testRef = collection(db, "companies", companyInfo.id, "testemunhas");
        const testSnap = await getDocs(testRef);
        const tData = testSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBancoTestemunhas(tData);
      } catch (err) {
        console.error("Erro ao buscar banco de testemunhas:", err);
      }
    };
    fetchTestemunhas();
  }, [companyInfo?.id]);

  useEffect(() => {
    if (!protocoloReal || !companyInfo?.id) return;

    setLoading(true);

    const q = query(
      collection(db, "requests"), 
      where("protocolo", "==", protocoloReal),
      where("companyId", "==", companyInfo.id)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      if (!snapshot.empty) {
        const docData = snapshot.docs[0];
        const data = docData.data();
        const mainData = { id: docData.id, ...data };
        setSolicitacao(mainData);

        // Mapeamento alinhado ao novo roadmap
        if (data.contractStatus) {
           setContractStatus(data.contractStatus);
        } else if (data.status === "Garantia Ativa") {
           setContractStatus("closed");
        } else {
           setContractStatus(null);
        }

        try {
          const partesRef = collection(db, `requests/${docData.id}/partes`);
          const partesSnapshot = await getDocs(partesRef);
          const partesData = partesSnapshot.docs.map((pDoc) => ({ id: pDoc.id, ...pDoc.data() }));
          setPartes(partesData);
        } catch (err) {
          console.warn("⚠️ Aviso: Não foi possível carregar as Partes.", err);
          setPartes([]); 
        }
      } else {
        setSolicitacao(null);
      }
      setLoading(false);
    }, (error) => {
      console.error("Erro ao buscar detalhes:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [protocoloReal, companyInfo?.id]);

  const abrirModalDocs = (/** @type {any} */ parte) => {
    setParteSelecionada(parte);
    setIsDocModalOpen(true);
  };

  // ============================================================================
  // GESTÃO DE SIGNATÁRIOS E ARQUIVOS (ETAPA 2 ROADMAP)
  // ============================================================================
  const handleMultipleFilesChange = (e, setFilesState) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((f) => f.type === "application/pdf");
    
    if (files.length !== validFiles.length) {
      alert("Apenas arquivos no formato PDF são permitidos.");
    }
    
    setFilesState((prev) => [...prev, ...validFiles]);
    e.target.value = ""; 
  };

  const removeFile = (index, setFilesState) => {
    setFilesState((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddTestemunha = (e) => {
    const tId = e.target.value;
    if (!tId) return;
    const testem = bancoTestemunhas.find((t) => t.id === tId);
    if (testem && !selectedTestemunhas.some((st) => st.id === tId)) {
      setSelectedTestemunhas((prev) => [...prev, testem]);
    }
    e.target.value = ""; 
  };

  const removeTestemunha = (id) => {
    setSelectedTestemunhas((prev) => prev.filter((t) => t.id !== id));
  };

  // Filtra signatários automáticos da base (Partes do contrato)
  const signatariosAutomaticos = partes.filter((p) => p.dadosPessoais?.nome && p.dadosPessoais?.email);

  // ============================================================================
  // AÇÕES DO FLUXO DE ASSINATURA (NOVO ROADMAP)
  // ============================================================================

  const handleConfirmClickSign = async () => {
    if (clickSignFiles.length === 0) {
      alert("Por favor, anexe ao menos um documento para compor o Envelope ClickSign.");
      return;
    }

    if (signatariosAutomaticos.length === 0) {
      alert("Erro: Não foi possível identificar as partes (Locatário/Fiador) com e-mail cadastrado na ficha.");
      return;
    }

    setIsUpdatingStatus(true);
    
    try {
      const docRef = doc(db, "requests", solicitacao.id);
      const protocoloSafe = solicitacao.protocolo.replace(/\//g, "-");
      const timestamp = Date.now();
      const uploadedDocs = [];

      // 1. Upload dos arquivos físicos para o Storage (Preparando o terreno para o Backend)
      for (const file of clickSignFiles) {
        const fileRef = ref(
          storage,
          `garantias/${storageEnvironment}/${companyInfo.id}/${protocoloSafe}/clicksign/${timestamp}_${file.name}`
        );
        await uploadBytes(fileRef, file);
        const fileUrl = await getDownloadURL(fileRef);
        
        uploadedDocs.push({
          nome: file.name,
          url: fileUrl
        });
      }
      
      // Monta o payload estruturado (armazenado para histórico no BD da ficha e uso pelo Backend)
      const todosSignatarios = [
        ...signatariosAutomaticos.map((p) => ({
          nome: p.dadosPessoais.nome,
          email: p.dadosPessoais.email,
          cpf: p.dadosPessoais.cpf || "",
          tipo: p.dadosPessoais.pretendenteA || "Parte do Contrato",
          telefone: p.dadosPessoais.telefone || "",
          nascimento: p.dadosPessoais.dataNascimento || ""
        })),
        ...selectedTestemunhas.map((t) => ({
          nome: t.nome,
          email: t.email,
          cpf: t.cpf || "",
          tipo: "Testemunha"
        }))
      ];

      // 2. Atualiza dados locais com as URLs reais e signatários enriquecidos
      await updateDoc(docRef, { 
        envelopeFiles: uploadedDocs,
        envelopeSignatarios: todosSignatarios
      });
      
      // 3. 🚀 CHAMA O MOTOR CLICKSIGN (BACKEND)
      await gerarEnvelopeClickSign(solicitacao.id);

      // O Backend já muda o status para "running", mas forçamos localmente para feedback
      setContractStatus("running");
      setIsClickSignModalOpen(false);
      setClickSignFiles([]);
      setSelectedTestemunhas([]);
      
      alert("✅ Envelope gerado com sucesso! As partes já vão receber os links para assinatura.");

    } catch (error) {
      console.error("Erro ao iniciar ClickSign:", error);
      alert("Falha ao gerar o envelope na ClickSign. Verifique o console para mais detalhes.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleUploadSignedContract = async () => {
    if (signedFiles.length === 0 || !signatureDate) {
      alert("Por favor, anexe o(s) Contrato(s) de Locação assinado(s) em PDF e informe a data de assinatura.");
      return;
    }

    if (signatariosAutomaticos.length === 0) {
      alert("Erro: Não foi possível identificar as partes (Locatário/Fiador) com e-mail cadastrado na ficha.");
      return;
    }

    setIsUpdatingStatus(true);
    
    try {
      const docRef = doc(db, "requests", solicitacao.id);
      const protocoloSafe = solicitacao.protocolo.replace(/\//g, "-");
      const timestamp = Date.now();
      const uploadedDocs = [];

      // 1. Upload do Contrato Físico para o Storage (Pasta específica 'assinados')
      for (const file of signedFiles) {
        const fileRef = ref(
          storage,
          `garantias/${storageEnvironment}/${companyInfo.id}/${protocoloSafe}/assinados/${timestamp}_${file.name}`
        );
        await uploadBytes(fileRef, file);
        const fileUrl = await getDownloadURL(fileRef);
        
        uploadedDocs.push({
          nome: file.name,
          url: fileUrl
        });
      }

      // Monta o payload estruturado de signatários para a ClickSign
      const todosSignatarios = [
        ...signatariosAutomaticos.map((p) => ({
          nome: p.dadosPessoais.nome,
          email: p.dadosPessoais.email,
          cpf: p.dadosPessoais.cpf || "",
          tipo: p.dadosPessoais.pretendenteA || "Parte do Contrato",
          telefone: p.dadosPessoais.telefone || "",
          nascimento: p.dadosPessoais.dataNascimento || ""
        }))
        // Obs: Omitimos testemunhas no fluxo físico, pois já assinaram o contrato no papel.
      ];

      // 2. Atualiza o Firestore com as URLs físicas e a Flag Híbrida
      await updateDoc(docRef, { 
        dataAssinatura: signatureDate,
        documentosAssinados: uploadedDocs,
        dataUploadAssinados: new Date().toISOString(),
        envelopeSignatarios: todosSignatarios,
        isHybridFlow: true // 🚩 A FLAG MÁGICA: Avisa o backend para gerar o Semi-Envelope
      });
      
      // 3. CHAMA O MOTOR CLICKSIGN (BACKEND)
      // O backend vai ler a flag isHybridFlow e montar apenas os PDFs da Real Benefícios.
      await gerarEnvelopeClickSign(solicitacao.id);

      // O Backend já muda o status para "running", mas forçamos localmente para feedback visual imediato
      setContractStatus("running");
      setIsUploadSignedModalOpen(false);
      setSignedFiles([]);
      setSignatureDate("");

      alert("✅ Contrato físico salvo e links da Carta Fiança/Termo enviados aos inquilinos!");

    } catch (error) {
      console.error("Erro no fluxo Híbrido:", error);
      alert("Falha ao processar o envio. Verifique a conexão e o console.");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // ============================================================================
  // RENDERIZAÇÃO E MÁQUINA DE ESTADOS
  // ============================================================================

  if (loading) return <DetailsSkeleton />;

  if (!solicitacao) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-fade-in px-4">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-6 shadow-inner">
          <FileText className="w-10 h-10 text-slate-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Ficha Indisponível</h2>
        <p className="text-slate-500 mb-8 max-w-md font-medium">O protocolo <strong>{protocoloReal}</strong> não existe, foi excluído ou sua imobiliária não possui permissão para acessá-lo.</p>
        <button onClick={() => navigate(-1)} className="flex items-center justify-center gap-2 px-8 py-3 bg-brand-primary text-white rounded-full font-semibold hover:bg-teal-600 transition-all shadow-md hover:shadow-lg">
          <ArrowLeft className="w-4 h-4"/> Voltar para Lista
        </button>
      </div>
    );
  }

  const vAluguel = parseFloat(String(solicitacao.imovel?.aluguel || "0").replace(/\./g, "").replace(",", "."));
  const vCondominio = parseFloat(String(solicitacao.imovel?.condominio || "0").replace(/\./g, "").replace(",", "."));
  const vIptu = parseFloat(String(solicitacao.imovel?.iptu || "0").replace(/\./g, "").replace(",", "."));
  const vAgua = parseFloat(String(solicitacao.imovel?.agua || "0").replace(/\./g, "").replace(",", "."));
  const vLuz = parseFloat(String(solicitacao.imovel?.luz || "0").replace(/\./g, "").replace(",", "."));
  const vSeguroIncendio = parseFloat(String(solicitacao.imovel?.seguroIncendio || "0").replace(/\./g, "").replace(",", "."));
  const totalMensal = (vAluguel + vCondominio + vIptu + vAgua + vLuz + vSeguroIncendio).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

  // Variáveis Matemáticas de Faturamento (Fase 3 Roadmap)
  const formaPagamento = solicitacao.condicoesAprovacao?.formaPagamento;
  const valorTaxaMensal = solicitacao.condicoesAprovacao?.valorTaxa || "0,00";
  const taxaComissaoAtivacao = solicitacao.taxaAtivacao || "0,00";

  // 🟢 MÁQUINA DE ESTADOS VISUAIS
  const isDraft = contractStatus === "draft" || contractStatus === "running" || contractStatus === "awaiting_real_validation" || contractStatus === "closed" || solicitacao.status === "Garantia Ativa";
  const isRunning = contractStatus === "running" || contractStatus === "awaiting_real_validation" || contractStatus === "closed" || solicitacao.status === "Garantia Ativa";
  const isAwaitingValidation = contractStatus === "awaiting_real_validation" || contractStatus === "closed" || solicitacao.status === "Garantia Ativa";
  const isClosed = contractStatus === "closed" || solicitacao.status === "Garantia Ativa";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in bg-slate-50 min-h-full font-sans">
      
      {/* HEADER E BREADCRUMB */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
        <div className="flex items-center text-sm text-slate-500 font-medium">
          <Link to="/dashboard/solicitacoes" className="hover:text-brand-primary transition-colors">Solicitações</Link>
          <ChevronRight className="w-4 h-4 mx-1" />
          <span className="font-semibold text-slate-800 truncate max-w-[200px] sm:max-w-none">
            {solicitacao.protocolo}
          </span>
        </div>
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:text-brand-primary hover:border-brand-primary/50 transition-all shadow-sm w-fit shrink-0"
        >
          <ArrowLeft className="w-4 h-4"/> Voltar
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 relative overflow-hidden">
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <h1 className="text-xl font-bold text-slate-800">
              Ficha {solicitacao.protocolo}
            </h1>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border shadow-sm
              ${solicitacao.status === "Garantia Ativa" ? "bg-green-600 text-white border-green-700" :
                solicitacao.status === "Aprovada" ? "bg-green-100 text-green-700 border-green-200" : 
                solicitacao.status === "Rejeitada" ? "bg-red-100 text-red-700 border-red-200" : 
                solicitacao.status === "Pendente" ? "bg-yellow-100 text-yellow-700 border-yellow-200" : 
                "bg-blue-100 text-blue-700 border-blue-200"}`}>
              {solicitacao.status}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-500 font-medium">
            <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-brand-primary/70"/> <strong className="text-slate-700">{solicitacao.companyName}</strong></span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-brand-primary/70"/> {solicitacao.servico || "Fiança Locatícia"}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-brand-primary/70"/> {solicitacao.dataSolicitacaoTexto}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        {/* COLUNA ESQUERDA */}
        <div className="xl:col-span-1 space-y-6">

          {solicitacao.condicoesAprovacao && (
            <div className="bg-white rounded-2xl border-l-4 border-l-brand-primary border border-slate-200 shadow-sm p-6 animate-fade-in">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <ShieldCheck className="w-5 h-5 text-brand-primary"/> Condições de Aprovação
              </h2>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                    <Percent className="w-4 h-4 text-slate-400"/> Taxa Aprovada
                  </span>
                  <span className="text-sm font-bold text-slate-800">{solicitacao.condicoesAprovacao.percentual || "-"}</span>
                </div>
                <div className="flex justify-between items-center border-t border-slate-200 pt-3">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                    <DollarSign className="w-4 h-4 text-slate-400"/> Valor (Mensal)
                  </span>
                  <span className="text-sm font-bold text-brand-primary">R$ {solicitacao.condicoesAprovacao.valorTaxa || "-"}</span>
                </div>
                <div className="flex justify-between items-center border-t border-slate-200 pt-3">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
                    <CreditCard className="w-4 h-4 text-slate-400"/> Pagamento
                  </span>
                  <span className="text-sm font-bold text-slate-800 truncate max-w-[140px] text-right" title={solicitacao.condicoesAprovacao.formaPagamento}>
                    {solicitacao.condicoesAprovacao.formaPagamento || "-"}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
              <MapPin className="w-5 h-5 text-slate-400"/> Dados do Imóvel
            </h2>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">Endereço</p>
                <p className="text-sm font-semibold text-slate-800">
                  {solicitacao.imovel?.endereco}, {solicitacao.imovel?.numero}
                </p>
                <p className="text-sm font-medium text-slate-500 mt-1">
                  {solicitacao.imovel?.complemento ? `${solicitacao.imovel.complemento} - ` : ""} {solicitacao.imovel?.bairro}
                </p>
                <p className="text-sm font-medium text-slate-500 mt-0.5">
                  {solicitacao.imovel?.cidade}/{solicitacao.imovel?.estado} <span className="ml-2 font-medium bg-slate-100 px-2 py-1 rounded text-slate-600">CEP: {solicitacao.imovel?.cep}</span>
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Código</p>
                  <p className="text-sm font-semibold text-slate-800 font-mono bg-slate-50 px-2 py-1 rounded border border-slate-100 w-fit">{solicitacao.imovel?.codigo || "-"}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Tipo</p>
                  <p className="text-sm font-semibold text-slate-800 capitalize">{solicitacao.imovel?.tipo || "-"}</p>
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl space-y-2 mt-4 border border-slate-100">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500 font-medium text-xs">Aluguel:</span>
                  <span className="font-semibold text-slate-800">R$ {solicitacao.imovel?.aluguel || "0,00"}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500 font-medium text-xs">Condomínio:</span>
                  <span className="font-medium text-slate-800">R$ {solicitacao.imovel?.condominio || "0,00"}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500 font-medium text-xs">IPTU:</span>
                  <span className="font-medium text-slate-800">R$ {solicitacao.imovel?.iptu || "0,00"}</span>
                </div>
                {solicitacao.imovel?.agua && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 font-medium text-xs">Água:</span>
                    <span className="font-medium text-slate-800">R$ {solicitacao.imovel.agua}</span>
                  </div>
                )}
                {solicitacao.imovel?.luz && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 font-medium text-xs">Luz:</span>
                    <span className="font-medium text-slate-800">R$ {solicitacao.imovel.luz}</span>
                  </div>
                )}
                {solicitacao.imovel?.seguroIncendio && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 font-medium text-xs">Seguro Incêndio:</span>
                    <span className="font-medium text-slate-800">R$ {solicitacao.imovel.seguroIncendio}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 mt-2 border-t border-slate-200">
                  <span className="text-brand-primary font-semibold text-xs">Valor Locatício:</span>
                  <span className="font-bold text-brand-primary text-sm">R$ {totalMensal}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-3">
              <MessageSquare className="w-5 h-5 text-slate-400"/> Suas Observações
            </h2>
            {solicitacao.observacoes ? (
              <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-100 text-sm font-medium text-slate-800 italic leading-relaxed">
                "{solicitacao.observacoes}"
              </div>
            ) : (
              <p className="text-sm text-slate-400 font-medium italic">Nenhuma observação informada no momento do cadastro.</p>
            )}
          </div>

          {solicitacao.historico && solicitacao.historico.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-4 border-b border-slate-100 pb-3">
                <Clock className="w-5 h-5 text-slate-400"/> Histórico de Análises
              </h2>
              <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar pr-2">
                {[...solicitacao.historico].reverse().map((/** @type {any} */ hist, /** @type {number} */ idx) => (
                  <div key={idx} className={`p-4 rounded-xl border relative ${hist.status === "Rejeitada" ? "bg-red-50 border-red-100" : hist.status === "Pendente" ? "bg-yellow-50 border-yellow-100" : "bg-slate-50 border-slate-100"}`}>
                    <div className="flex justify-between items-start mb-2">
                      <span className={`text-xs font-semibold ${hist.status === "Rejeitada" ? "text-red-600" : hist.status === "Pendente" ? "text-yellow-600" : "text-slate-600"}`}>{hist.status}</span>
                      <span className="text-xs font-medium text-slate-400">{new Date(hist.data).toLocaleDateString("pt-BR")}</span>
                    </div>
                    <p className="text-sm text-slate-700 font-medium leading-relaxed">{hist.mensagem}</p>
                    <p className="text-xs text-slate-400 mt-3 text-right font-medium">Por: {hist.autor}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* COLUNA DIREITA */}
        <div className="xl:col-span-2 space-y-6">
          
          {solicitacao.status !== "Em Analise" && solicitacao.status !== "Pendente" && solicitacao.status !== "Rejeitada" && (
            <div className="bg-white rounded-2xl border-l-4 border-l-blue-500 border border-slate-200 shadow-sm p-6 animate-fade-in">
              <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-6 border-b border-slate-100 pb-3">
                <FileSignature className="w-5 h-5 text-blue-500"/> Gestão de Assinaturas & Faturamento
              </h2>

              <div className="space-y-0 relative">
                <div className="absolute top-4 bottom-8 left-4 w-0.5 bg-slate-200"></div>

                {/* PASSO 1: DOCUMENTOS DA GARANTIA */}
                <div className="flex gap-4 relative z-10 pb-8">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-white ${isDraft ? "bg-blue-500 text-white shadow-md" : "bg-slate-100 text-slate-400 border border-slate-200"}`}>
                    <FileText className="w-4 h-4"/>
                  </div>
                  <div className="pt-1.5 w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <p className={`font-semibold text-sm ${isDraft ? "text-slate-800" : "text-slate-400"}`}>Documentos da Garantia (RB)</p>
                        <p className="text-xs text-slate-500 font-medium mt-0.5">
                          {isDraft ? "A Real Benefícios preparou os modelos para o envelope." : "Aguardando a Real Benefícios gerar e anexar os documentos oficiais para liberar a assinatura."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* PASSO 2: ASSINATURAS DO CONTRATO */}
                <div className="flex gap-4 relative z-10 pb-8">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-white ${isRunning ? "bg-blue-500 text-white shadow-md" : isDraft ? "bg-white border-2 border-blue-500 text-blue-600" : "bg-slate-100 text-slate-400 border border-slate-200"}`}>
                    <Send className="w-4 h-4"/>
                  </div>
                  <div className="pt-1.5 w-full">
                    <p className={`font-semibold text-sm ${isRunning ? "text-slate-800" : isDraft ? "text-blue-700" : "text-slate-400"}`}>
                      Assinatura do Contrato de Locação
                    </p>

                    {/* SELEÇÃO DO MÉTODO DE ASSINATURA */}
                    {isDraft && !isRunning && (
                      <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-5">
                        <p className="text-sm font-semibold text-slate-700 mb-4 text-center">Como deseja colher as assinaturas deste contrato?</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <button 
                            disabled={isUpdatingStatus}
                            onClick={() => setIsClickSignModalOpen(true)}
                            className="flex flex-col items-center justify-center gap-2 p-4 bg-white border border-blue-200 rounded-xl hover:border-blue-500 hover:shadow-md transition-all group disabled:opacity-50"
                          >
                            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                              <Zap className="w-5 h-5"/>
                            </div>
                            <span className="text-sm font-bold text-slate-800">Assinatura Digital</span>
                            <span className="text-xs text-slate-500 font-medium text-center">Adicionar documentos da imobiliária ao envelope da Real.</span>
                          </button>

                          <button 
                            disabled={isUpdatingStatus}
                            onClick={() => setIsUploadSignedModalOpen(true)}
                            className="flex flex-col items-center justify-center gap-2 p-4 bg-white border border-slate-200 rounded-xl hover:border-brand-primary hover:shadow-md transition-all group disabled:opacity-50"
                          >
                            <div className="w-10 h-10 bg-slate-100 text-slate-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                              <FileSignature className="w-5 h-5"/>
                            </div>
                            <span className="text-sm font-bold text-slate-800">Anexar Contrato e Gerar ClickSign</span>
                            <span className="text-xs text-slate-500 font-medium text-center">Salvar contrato físico da imobiliária e disparar Carta Fiança (RB).</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 🟢 ESTADO RUNNING (AGUARDANDO ASSINATURAS CLICKSIGN) */}
                    {isRunning && !isAwaitingValidation && (
                      <div className="mt-3 bg-yellow-50 border border-yellow-200 p-4 rounded-xl flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-yellow-800">Envelope em Execução (ClickSign)</p>
                          <p className="text-xs text-yellow-700 font-medium mt-1">Aguardando os signatários finalizarem o processo.</p>
                        </div>
                        <Loader2 className="w-5 h-5 text-yellow-600 animate-spin" />
                      </div>
                    )}
                    
                    {!isDraft && (
                      <p className="text-xs text-slate-500 font-medium mt-0.5">Aguardando a etapa anterior ser concluída.</p>
                    )}
                  </div>
                </div>

                {/* PASSO 3: VALIDAÇÃO DA REAL BENEFÍCIOS */}
                <div className="flex gap-4 relative z-10 pb-8">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-white ${isAwaitingValidation ? "bg-blue-500 text-white shadow-md" : "bg-slate-100 text-slate-400 border border-slate-200"}`}>
                    <Search className="w-4 h-4"/>
                  </div>
                  <div className="pt-1.5 w-full">
                    <p className={`font-semibold text-sm ${isAwaitingValidation ? "text-slate-800" : "text-slate-400"}`}>
                      Auditoria Real Benefícios
                    </p>

                    {isAwaitingValidation && !isClosed ? (
                      <div className="mt-3 bg-blue-50 border border-blue-200 p-4 rounded-xl flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-blue-800">Aguardando Ateste</p>
                          <p className="text-xs text-blue-700 font-medium mt-1">As assinaturas foram colhidas. A Real Benefícios está revisando os documentos antes de ativar a garantia.</p>
                        </div>
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 font-medium mt-0.5">
                        {!isAwaitingValidation ? "Aguardando conclusão das assinaturas." : "Documentação homologada com sucesso."}
                      </p>
                    )}
                  </div>
                </div>

                {/* PASSO 4: CONCLUSÃO E FATURAMENTO ASAAS */}
                <div className="flex gap-4 relative z-10">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-white ${isClosed ? "bg-green-500 text-white shadow-md" : "bg-slate-100 text-slate-400 border border-slate-200"}`}>
                    <CheckCircle className="w-4 h-4"/>
                  </div>
                  <div className="pt-1.5 w-full">
                    <p className={`font-semibold text-sm ${isClosed ? "text-green-600" : "text-slate-400"}`}>Conclusão Legal & Ativação (Asaas)</p>
                    
                    {!isClosed ? (
                       <p className="text-xs text-slate-500 font-medium mt-0.5">
                         Aguardando validação final para liberação do contrato e faturamento.
                       </p>
                    ) : (
                      <div className="mt-4 bg-green-600 rounded-2xl border border-green-700 shadow-md p-6 text-white animate-fade-in relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-20">
                          <DollarSign className="w-24 h-24 rotate-12" />
                        </div>
                        <div className="relative z-10">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="bg-white/20 p-2 rounded-lg">
                              <CreditCard className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-base font-bold">Garantia Locatícia Ativada</h2>
                          </div>
                          
                          <p className="text-sm text-green-50 font-medium mb-4 leading-relaxed">
                            O processo de auditoria foi concluído e a garantia legal está vigente. 
                          </p>

                          {/* REFLEXO DO ASAAS E MATEMÁTICA FASE 3 */}
                          <div className="bg-black/15 rounded-xl p-4 mb-2 border border-white/10">
                            
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 border-b border-white/10 pb-3">
                              <div>
                                <p className="text-xs text-green-100 uppercase tracking-wide font-semibold mb-1">Status da Cobrança (Ativação)</p>
                                {solicitacao.paymentStatus === "paid" ? (
                                  <p className="text-lg font-bold flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5"/> Pagamento Confirmado
                                  </p>
                                ) : (
                                  <p className="text-lg font-bold flex items-center gap-2 text-yellow-300">
                                    <Clock className="w-5 h-5"/> Boleto Emitido - Aguardando
                                  </p>
                                )}
                              </div>
                              <div className="text-left sm:text-right mt-3 sm:mt-0">
                                 <p className="text-xs text-green-100 uppercase tracking-wide font-semibold mb-1">Total do Boleto (Inquilino)</p>
                                 <p className="text-xl font-bold">
                                   R$ {formaPagamento === "Boleto Imobiliária" ? taxaComissaoAtivacao : calcularSoma(taxaComissaoAtivacao, valorTaxaMensal)}
                                 </p>
                              </div>
                            </div>

                            <div className="space-y-1.5 mt-2">
                              <p className="text-xs font-semibold text-green-100 mb-2">Descritivo (Forma: {formaPagamento || "Não definida"})</p>
                              {formaPagamento === "Boleto Imobiliária" ? (
                                <>
                                  <div className="flex justify-between text-xs text-green-50">
                                    <span>Taxa de Ativação (Comissão)</span>
                                    <span>R$ {taxaComissaoAtivacao}</span>
                                  </div>
                                  <div className="flex justify-between text-xs text-green-50/50 line-through">
                                    <span>1ª Mensalidade da Garantia</span>
                                    <span>R$ {valorTaxaMensal}</span>
                                  </div>
                                  <p className="text-[10px] text-green-200 mt-2 italic font-medium leading-relaxed">* O valor da mensalidade será embutido no faturamento B2B mensal da sua imobiliária. O inquilino pagará apenas a ativação agora.</p>
                                </>
                              ) : (
                                <>
                                  <div className="flex justify-between text-xs text-green-50">
                                    <span>Taxa de Ativação (Comissão)</span>
                                    <span>R$ {taxaComissaoAtivacao}</span>
                                  </div>
                                  <div className="flex justify-between text-xs text-green-50">
                                    <span>1ª Mensalidade da Garantia</span>
                                    <span>R$ {valorTaxaMensal}</span>
                                  </div>
                                  <p className="text-[10px] text-green-200 mt-2 italic font-medium leading-relaxed">* O Inquilino pagará a soma da Ativação + 1ª Mensalidade no mesmo boleto/pagamento.</p>
                                </>
                              )}
                            </div>

                          </div>
                          
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 h-full">
            <h2 className="text-base font-bold text-slate-800 flex items-center gap-2 mb-6 border-b border-slate-100 pb-3">
              <Users className="w-5 h-5 text-brand-primary"/> Partes Cadastradas ({partes.length})
            </h2>
            <div className="space-y-4">
              {partes.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium text-sm">Nenhuma parte cadastrada localizada.</p>
                </div>
              ) : (
                partes.map((/** @type {any} */ parte) => (
                  <div key={parte.id} className="border border-slate-200 rounded-xl p-5 bg-white hover:border-brand-primary/30 transition-colors shadow-sm">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary font-semibold text-lg shrink-0 shadow-inner">
                          {parte.dadosPessoais?.nome?.charAt(0) || "?"}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800 text-sm">
                            {parte.dadosPessoais?.nome}
                          </h3>
                          <div className="flex flex-wrap gap-2 text-xs text-slate-500 mt-1.5 items-center font-medium">
                            <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded border border-slate-200">
                              {parte.dadosPessoais?.pretendenteA || "Não informado"}
                            </span>
                            <span className="bg-white px-2 py-1 rounded border border-slate-200">
                              CPF: {parte.dadosPessoais?.cpf}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end border-t sm:border-t-0 border-slate-100 pt-4 sm:pt-0">
                        <span className="text-xs font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-blue-700 bg-blue-50 border border-blue-100">
                          <FileText className="w-4 h-4"/> {parte.documentos?.length || 0} arquivos
                        </span>
                        <button 
                          onClick={() => abrirModalDocs(parte)}
                          className="px-5 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-brand-primary hover:bg-brand-primary hover:text-white transition-colors shadow-sm shrink-0"
                        >
                          Visualizar
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

      </div>

      {isDocModalOpen && parteSelecionada && createPortal(
        <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-6 animate-fade-in" style={{ zIndex: 99999 }}>
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsDocModalOpen(false)}></div>
          <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 max-h-[90vh]">
            
            <div className="p-6 border-b border-slate-200 flex justify-between items-start sm:items-center bg-white shrink-0">
              <div className="flex-1 min-w-0 pr-4">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-brand-primary shrink-0"/> Arquivos Anexados
                </h2>
                <p className="text-sm text-slate-500 font-medium mt-1 truncate" title={parteSelecionada.dadosPessoais?.nome}>
                  {parteSelecionada.dadosPessoais?.nome}
                </p>
              </div>
              <button onClick={() => setIsDocModalOpen(false)} className="text-slate-400 hover:text-slate-700 p-2 bg-slate-50 border border-slate-200 rounded-full transition-colors shadow-sm shrink-0">
                <X className="w-5 h-5"/>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-3 custom-scrollbar flex-1 bg-slate-50">
              {!parteSelecionada.documentos || parteSelecionada.documentos.length === 0 ? (
                <div className="py-12 text-center text-slate-400 font-medium border-2 border-dashed border-slate-200 rounded-xl bg-white text-sm">
                  Nenhum arquivo foi enviado para esta parte.
                </div>
              ) : (
                parteSelecionada.documentos.map((/** @type {any} */ docItem, /** @type {number} */ idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border border-slate-200 rounded-xl bg-white shadow-sm gap-4 hover:border-brand-primary/30 transition-colors">
                    
                    <div className="flex flex-col flex-1 min-w-0 w-full">
                      <p className="font-semibold text-sm text-slate-800 truncate w-full" title={docItem.nome}>
                        {docItem.nome}
                      </p>
                      <span className="inline-block mt-1.5 px-2 py-1 bg-slate-100 text-xs font-medium text-slate-500 rounded w-fit border border-slate-200">
                        {docItem.tipo}
                      </span>
                    </div>

                    <a 
                      href={docItem.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center gap-2 px-5 py-2 bg-brand-primary text-white hover:bg-teal-600 rounded-lg transition-colors font-semibold text-sm shadow-md shadow-brand-primary/20 w-full sm:w-auto justify-center shrink-0"
                    >
                      Abrir Documento <ExternalLink className="w-4 h-4"/>
                    </a>
                  </div>
                ))
              )}
            </div>
            
            <div className="p-6 border-t border-slate-200 bg-white flex justify-end shrink-0">
               <button onClick={() => setIsDocModalOpen(false)} className="px-8 py-2.5 text-sm font-semibold bg-slate-100 text-slate-600 border border-slate-200 rounded-full transition-colors hover:bg-slate-200 shadow-sm">
                Fechar
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* MODAL 1: ASSINATURA DIGITAL (CLICKSIGN) - ETAPA 2 ROADMAP */}
      {isClickSignModalOpen && createPortal(
        <div className="fixed inset-0 flex items-center justify-center p-4 xl:p-8 animate-fade-in overflow-hidden" style={{ zIndex: 99999 }}>
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => !isUpdatingStatus && setIsClickSignModalOpen(false)}></div>
          
          <div className="relative bg-white w-full max-w-2xl flex flex-col rounded-2xl shadow-2xl border border-slate-200 overflow-hidden max-h-full">
            
            <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-blue-50 shrink-0">
              <div>
                <h2 className="text-lg font-bold text-blue-800 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-600"/> Envelope ClickSign
                </h2>
                <p className="text-sm text-blue-600 font-medium mt-1">Configure os arquivos e signatários</p>
              </div>
              <button 
                disabled={isUpdatingStatus}
                onClick={() => setIsClickSignModalOpen(false)} 
                className="text-slate-400 hover:text-slate-600 p-2 bg-white border border-slate-200 rounded-full transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5"/>
              </button>
            </div>

            <div className="p-5 bg-white space-y-6 overflow-y-auto custom-scrollbar flex-1 min-h-0">
              
              {/* BLOCO 1: ARQUIVOS */}
              <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 relative group hover:border-blue-400 transition-colors shadow-sm">
                <label className="flex flex-col text-sm font-semibold text-slate-700 mb-3 cursor-pointer">
                  <span className="flex items-center gap-2"><Files className="w-4 h-4 text-blue-600"/> 1. Anexar Contrato de Locação e Outros (PDF)</span>
                  <span className="text-xs font-medium text-slate-500 mt-1">Os arquivos abaixo serão adicionados ao envelope base gerado pela Real Benefícios.</span>
                </label>
                <input 
                  type="file" 
                  accept=".pdf"
                  multiple
                  onChange={(e) => handleMultipleFilesChange(e, setClickSignFiles)}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2.5 file:px-5 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 cursor-pointer"
                  disabled={isUpdatingStatus}
                />
              </div>

              {clickSignFiles.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-inner max-h-40 overflow-y-auto custom-scrollbar">
                  <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Arquivos do Envelope ({clickSignFiles.length})</p>
                  <ul className="space-y-2">
                    {clickSignFiles.map((file, index) => (
                      <li key={index} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded border border-slate-100">
                        <span className="text-xs font-medium text-slate-700 truncate mr-2" title={file.name}>{file.name}</span>
                        <button 
                          disabled={isUpdatingStatus}
                          onClick={() => removeFile(index, setClickSignFiles)}
                          className="text-red-400 hover:text-red-600 p-1 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* BLOCO 2: SIGNATÁRIOS */}
              <div className="pt-2">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3 border-b border-slate-100 pb-2">
                  <Users className="w-4 h-4 text-slate-500"/> 2. Definir Signatários
                </h3>

                {/* Signatários Automáticos */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Partes do Contrato (Automático)</p>
                  {signatariosAutomaticos.length === 0 ? (
                    <p className="text-xs text-red-500 bg-red-50 p-2 rounded border border-red-100">Nenhum locatário/fiador com e-mail cadastrado foi encontrado na ficha.</p>
                  ) : (
                    <ul className="space-y-2">
                      {signatariosAutomaticos.map((p) => (
                        <li key={p.id} className="flex flex-col bg-slate-50 px-3 py-2.5 rounded-lg border border-slate-200 shadow-sm">
                          <span className="text-sm font-semibold text-slate-800">{p.dadosPessoais?.nome}</span>
                          <span className="text-xs text-slate-500">{p.dadosPessoais?.email} • {p.dadosPessoais?.pretendenteA || "Parte"}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Adicionar Testemunhas */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                    Adicionar Testemunhas
                  </label>
                  <select 
                    onChange={handleAddTestemunha}
                    defaultValue=""
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary transition-colors cursor-pointer"
                    disabled={isUpdatingStatus || bancoTestemunhas.length === 0}
                  >
                    <option value="" disabled>-- Selecione do banco de testemunhas --</option>
                    {bancoTestemunhas.map((t) => (
                      <option key={t.id} value={t.id}>{t.nome} ({t.email})</option>
                    ))}
                  </select>
                  {bancoTestemunhas.length === 0 && (
                    <p className="text-[10px] text-slate-400 mt-1">Cadastre testemunhas no menu lateral "Testemunhas".</p>
                  )}
                </div>

                {selectedTestemunhas.length > 0 && (
                  <ul className="space-y-2 mt-3">
                    {selectedTestemunhas.map((t) => (
                      <li key={t.id} className="flex items-center justify-between bg-blue-50 px-3 py-2.5 rounded-lg border border-blue-100 shadow-sm">
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold text-blue-900 truncate">{t.nome} <span className="text-[10px] bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded ml-1">Testemunha</span></span>
                          <span className="text-xs text-blue-700 truncate">{t.email}</span>
                        </div>
                        <button 
                          disabled={isUpdatingStatus}
                          onClick={() => removeTestemunha(t.id)}
                          className="text-blue-400 hover:text-red-500 p-1.5 rounded transition-colors shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

              </div>

            </div>

            <div className="p-5 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 shrink-0">
              <button 
                disabled={isUpdatingStatus}
                onClick={() => setIsClickSignModalOpen(false)} 
                className="px-6 py-2.5 text-sm font-semibold text-slate-600 bg-white hover:bg-slate-100 rounded-full transition-colors border border-slate-200 shadow-sm disabled:opacity-50"
              >
                Cancelar
              </button>
              <button 
                onClick={handleConfirmClickSign} 
                disabled={isUpdatingStatus || clickSignFiles.length === 0 || signatariosAutomaticos.length === 0}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-full transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingStatus ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
                {isUpdatingStatus ? "Iniciando..." : "Iniciar Assinaturas"}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* MODAL 2: ASSINATURA FÍSICA (UPLOAD MANUAL) */}
      {isUploadSignedModalOpen && createPortal(
        <div className="fixed inset-0 flex items-center justify-center p-4 xl:p-8 animate-fade-in overflow-hidden" style={{ zIndex: 99999 }}>
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => !isUpdatingStatus && setIsUploadSignedModalOpen(false)}></div>
          
          <div className="relative bg-white w-full max-w-lg flex flex-col rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-white shrink-0">
              <div>
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <UploadCloud className="w-5 h-5 text-brand-primary"/> Upload de Contrato Físico
                </h2>
                <p className="text-sm text-slate-500 font-medium mt-1">Geração de Garantia Manual</p>
              </div>
              <button 
                disabled={isUpdatingStatus}
                onClick={() => setIsUploadSignedModalOpen(false)} 
                className="text-slate-400 hover:text-slate-600 p-2 bg-slate-50 border border-slate-200 rounded-full transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5"/>
              </button>
            </div>

            <div className="p-6 bg-slate-50 space-y-6">
              
              <div className="border border-slate-200 rounded-xl p-5 bg-white relative group hover:border-brand-primary transition-colors shadow-sm">
                <label className="flex flex-col text-sm font-semibold text-slate-700 mb-3 cursor-pointer">
                  <span className="flex items-center gap-2"><Files className="w-4 h-4 text-brand-primary"/> 1. Anexar Contrato(s) Assinado(s) (PDF)</span>
                  <span className="text-xs font-medium text-slate-500 mt-1">O documento deve conter as assinaturas de todas as partes para revisão da Real.</span>
                </label>
                <input 
                  type="file" 
                  accept=".pdf"
                  multiple
                  onChange={(e) => handleMultipleFilesChange(e, setSignedFiles)}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2.5 file:px-5 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-primary/10 file:text-brand-primary hover:file:bg-brand-primary/20 cursor-pointer"
                  disabled={isUpdatingStatus}
                />
              </div>

              {signedFiles.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-inner max-h-40 overflow-y-auto custom-scrollbar">
                  <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Arquivos Selecionados ({signedFiles.length})</p>
                  <ul className="space-y-2">
                    {signedFiles.map((file, index) => (
                      <li key={index} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded border border-slate-100">
                        <span className="text-xs font-medium text-slate-700 truncate mr-2" title={file.name}>{file.name}</span>
                        <button 
                          disabled={isUpdatingStatus}
                          onClick={() => removeFile(index, setSignedFiles)}
                          className="text-red-400 hover:text-red-600 p-1 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border border-slate-200 rounded-xl p-5 bg-white shadow-sm">
                <label className="flex flex-col text-sm font-semibold text-slate-700 mb-3">
                  <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-brand-primary"/> 2. Data de Assinatura do Contrato</span>
                  <span className="text-xs font-medium text-slate-500 mt-1">Esta data definirá o início do ciclo de cobrança da garantia.</span>
                </label>
                <input 
                  type="date" 
                  value={signatureDate}
                  onChange={(e) => setSignatureDate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-800 focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
                  disabled={isUpdatingStatus}
                />
              </div>

            </div>

            <div className="p-6 border-t border-slate-200 bg-white flex justify-end gap-3 shrink-0">
              <button 
                disabled={isUpdatingStatus}
                onClick={() => setIsUploadSignedModalOpen(false)} 
                className="px-6 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors border border-slate-200 shadow-sm disabled:opacity-50"
              >
                Cancelar
              </button>
              <button 
                onClick={handleUploadSignedContract} 
                disabled={isUpdatingStatus || signedFiles.length === 0 || !signatureDate}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-brand-primary hover:bg-teal-600 rounded-full transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingStatus ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle className="w-4 h-4"/>}
                {isUpdatingStatus ? "Enviando..." : "Enviar para Revisão"}
              </button>
            </div>
          </div>
        </div>
      , document.body)}

    </div>
  );
};

export default SolicitacaoDetalhesPage;